import { setAppName } from '@/composables/usePageTitle.js';
import apiClient from '@/utils/axios.js';
import { defineStore } from 'pinia';

export const DEFAULT_COMPANY_LOGO = '/uploads/company/default-logo.png';
const DEFAULT_FAVICON = '/favicon.ico';

const isDefaultCompanyLogo = (logo) => {
    if (!logo) return true;
    return String(logo).includes('default-logo.png');
};

/**
 * Read a configuration row as a boolean, using the same truthy vocabulary the
 * server does. `fallback` is what a missing row means: for the optional header
 * taxes that is `true`, matching App\Services\TaxComponentSettings — an
 * installation that has never opened the settings screen must keep charging
 * exactly what it charges today.
 */
const configFlagOn = (value, fallback = false) => {
    if (value === undefined || value === null || value === '') return fallback;
    return ['1', 'true', 'yes', 'on'].includes(String(value).trim().toLowerCase());
};

const applyDocumentFavicon = (href) => {
    if (typeof document === 'undefined') return;

    const links = document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']");
    if (links.length) {
        links.forEach((link) => {
            link.setAttribute('href', href);
        });
        return;
    }

    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = href;
    document.head.appendChild(link);
};

export const useConfigurationStore = defineStore('configuration', {
    state: () => ({
        configurations: {},
        bankAccounts: [],
        addonFeatures: [],
        enabledAddonKeys: [],
        loaded: false,
        addonsLoaded: false,
        /** In-flight `/configurations/index` promise — see fetchConfigurations. */
        configurationsRequest: null,
    }),
    getters: {
        companyLogo: (state) => {
            const logo = state.configurations.company_logo;
            return logo && !isDefaultCompanyLogo(logo) ? logo : DEFAULT_COMPANY_LOGO;
        },
        companyLogoWithVersion: (state) => {
            const logo = state.configurations.company_logo;
            const resolved = logo || DEFAULT_COMPANY_LOGO;
            return resolved + (resolved.includes('?') ? '&' : '?') + 't=' + Date.now();
        },
        hasCustomCompanyLogo: (state) => {
            return !isDefaultCompanyLogo(state.configurations.company_logo);
        },
        company_name: (state) => state.configurations.company_name || 'Inventree',
        // FBR advance income tax. Only ever used to decide whether to *show* the
        // advance-tax row — the rate that is charged is always the one the
        // server resolved for the buyer, never one derived from these.
        advanceTaxEnabled: (state) => configFlagOn(state.configurations?.advance_tax_enabled),
        // The optional header taxes. Off means the box is hidden and the rate is
        // forced to zero — the server refuses a disabled component outright, so
        // the screens must never quote one.
        furtherTaxEnabled: (state) => configFlagOn(state.configurations?.further_tax_enabled, true),
        fedTaxEnabled: (state) => configFlagOn(state.configurations?.fed_tax_enabled, true),
        extraTaxEnabled: (state) => configFlagOn(state.configurations?.extra_tax_enabled, true),
        advanceTaxRates: (state) => ({
            '236G': {
                filer: parseFloat(state.configurations?.advance_tax_236g_filer || 0),
                non_filer: parseFloat(state.configurations?.advance_tax_236g_non_filer || 0),
            },
            '236H': {
                filer: parseFloat(state.configurations?.advance_tax_236h_filer || 0),
                non_filer: parseFloat(state.configurations?.advance_tax_236h_non_filer || 0),
            },
        }),
        // The buy side of the same sections: whether suppliers collect
        // s.236G/236H from us. Separate from advanceTaxEnabled, which is about
        // what we collect from buyers — an installation may do either alone.
        purchaseAdvanceTaxEnabled: (state) => configFlagOn(state.configurations?.purchase_advance_tax_enabled),
        /**
         * What a purchase should pre-fill its advance tax with: the section we
         * are billed under and the rate our own ATL standing attracts.
         *
         * A default the operator can overwrite, because the figure that counts
         * is the one printed on the supplier's invoice. The server resolves the
         * same pair and refuses the charge outright when the setting is off.
         */
        purchaseAdvanceTaxDefaults(state) {
            const section = state.configurations?.purchase_advance_tax_section === '236G' ? '236G' : '236H';
            const isFiler = configFlagOn(state.configurations?.purchase_advance_tax_filer, true);
            return { section, isFiler, rate: this.advanceTaxRates[section][isFiler ? 'filer' : 'non_filer'] };
        },
        currency: (state) => state.configurations?.currency || 'PKR',
        // Whether the floating Ask bubble follows you around. Defaults ON,
        // because the addon is already the gate that decides whether anyone has
        // Ask at all — an operator who has licensed it almost certainly wants
        // it reachable, and this switch is for the ones who find it in the way.
        askLauncherEnabled: (state) => configFlagOn(state.configurations?.ask_launcher_enabled, true),
        isAddonEnabled: (state) => (key) => state.enabledAddonKeys.includes(key),
    },

    actions: {
        applyFavicon() {
            const logo = this.configurations.company_logo;
            if (logo && !isDefaultCompanyLogo(logo)) {
                applyDocumentFavicon(logo + (logo.includes('?') ? '&' : '?') + 't=' + Date.now());
                return;
            }
            applyDocumentFavicon(DEFAULT_FAVICON);
        },
        loadFromCache() {
            const cached = localStorage.getItem('configurations');
            if (cached) {
                this.configurations = JSON.parse(cached);
                this.applyFavicon();
                setAppName(this.company_name);
            }
            const cachedAddons = localStorage.getItem('addonFeatures');
            if (cachedAddons) {
                try {
                    const parsed = JSON.parse(cachedAddons);
                    this.addonFeatures = parsed.features || [];
                    this.enabledAddonKeys = parsed.enabled || [];
                } catch (e) {
                    // ignore corrupt cache
                }
            }
        },
        setBankAccounts(accounts) {
            this.bankAccounts = accounts;
        },

        /**
         * Shared while in flight, because two callers race on every boot.
         *
         * App.vue's login watcher fires this during parent setup, and the
         * sidebar's `onMounted` fires it again a moment later — its guard is
         * `if (!configStore.loaded)`, and `loaded` is only set on the *response*,
         * which has not arrived yet. So every page load, on both shells, ran two
         * identical `/configurations/index` queries. Deduping here rather than by
         * having `loadFromCache` set `loaded` keeps the cache and the network
         * answer distinct: a cached copy is worth rendering, not worth trusting
         * as current.
         */
        async fetchConfigurations() {
            if (this.configurationsRequest) return this.configurationsRequest;

            this.configurationsRequest = this.requestConfigurations().finally(() => {
                this.configurationsRequest = null;
            });

            return this.configurationsRequest;
        },

        async requestConfigurations() {
            try {
                const response = await apiClient.get('/configurations/index');
                const newConfigs = {};
                for (const config of response.data) {
                    newConfigs[config.name] = config.value;
                }
                this.configurations = newConfigs;
                localStorage.setItem('configurations', JSON.stringify(newConfigs));
                this.loaded = true;
                this.applyFavicon();
                setAppName(this.company_name);
            } catch (error) {
                console.error('Failed to fetch configurations', error);
            }
        },

        async fetchAddonFeatures() {
            try {
                const response = await apiClient.get('/addon-features/index');
                this.addonFeatures = response.data?.data || [];
                this.enabledAddonKeys = response.data?.enabled || [];
                localStorage.setItem(
                    'addonFeatures',
                    JSON.stringify({
                        features: this.addonFeatures,
                        enabled: this.enabledAddonKeys,
                    }),
                );
                this.addonsLoaded = true;
            } catch (error) {
                console.error('Failed to fetch addon features', error);
            }
        },

        setConfigurations(configs) {
            this.configurations = configs;
            localStorage.setItem('configurations', JSON.stringify(configs));
            this.applyFavicon();
            setAppName(this.company_name);
        },
    },
});
